import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import { useForm, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import { AddStoryPayload, Person } from '../../../../types';
import { topTags } from '../constants';
import { useAuth } from '../../../../components/auth/AuthProvider';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../../utils';
import {
  maxMediaFiles,
  textLimits,
} from '../../../../core/constants/constants';

type AddStoryProps = {
  closeModal: () => void;
  onAddStory: (payload: AddStoryPayload | FormData) => void;
  treeId: string;
  treeMembers?: Person[];
};

type FormValues = {
  title: string;
  content: string;
  story_date: string;
  story_location: string;
  tags: string[];
  member_ids: string[];
  is_public: boolean;
};

export default function AddStoryToTree({
  closeModal,
  onAddStory,
  treeId,
  treeMembers = [],
}: AddStoryProps) {
  const { currentUser } = useAuth();
  const { control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      title: '',
      content: '',
      story_date: '',
      story_location: '',
      tags: [],
      member_ids: [],
    },
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string>('');
  const hiddenFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleMediaSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const acceptedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
      'audio/aac',
      'audio/mp3',
      'audio/mpeg',
      'audio/mp4',
      'audio/ogg',
      'audio/wav',
      'audio/x-m4a',
      'audio/x-wav',
    ];

    const validFiles = files.filter((file) =>
      acceptedTypes.includes(file.type),
    );

    const invalidFiles = files.filter(
      (file) => !validFiles.some((validFile) => validFile.name === file.name),
    );

    const imageFiles = validFiles.filter((file) =>
      file.type.startsWith('image/'),
    );
    const audioFiles = validFiles.filter((file) =>
      file.type.startsWith('audio/'),
    );

    if (imageFiles.length > maxMediaFiles) {
      setUploadError(`Please choose up to ${maxMediaFiles} photos per story.`);
      event.target.value = '';
      return;
    }

    const oversizedImage = imageFiles.some(
      (file) => file.size > 25 * 1024 * 1024,
    );
    const oversizedAudio = audioFiles.some(
      (file) => file.size > 50 * 1024 * 1024,
    );

    if (oversizedImage) {
      setUploadError('Image files must be 25MB or smaller.');
      event.target.value = '';
      return;
    }

    if (oversizedAudio) {
      setUploadError('Audio files must be 50MB or smaller.');
      event.target.value = '';
      return;
    }

    if (invalidFiles.length > 0) {
      setUploadError('Only photos and voice recordings are supported.');
      event.target.value = '';
      return;
    }

    setUploadError('');
    setSelectedFiles(validFiles);
    event.target.value = '';
  };

  function onSubmit(data: any) {
    if (!currentUser) {
      return;
    }

    const hasFiles = selectedFiles.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      formData.append('title', sanitiseNameInput(data.title));
      formData.append('content', sanitiseTextInput(data.content));
      formData.append(
        'story_date',
        data.story_date ? sanitiseNameInput(data.story_date) : '',
      );
      formData.append(
        'story_location',
        data.story_location ? sanitiseTextInput(data.story_location) : '',
      );
      formData.append('tree_id', treeId);
      formData.append('created_by', currentUser.uid);
      formData.append('is_public', String(Boolean(data.is_public)));
      (data.tags ?? []).forEach((tag: string) => formData.append('tags', tag));
      (data.member_ids ?? []).forEach((memberId: string) =>
        formData.append('member_ids', memberId),
      );
      selectedFiles.forEach((file) => formData.append('media_files', file));
      onAddStory(formData);
      return;
    }

    const payload: AddStoryPayload = {
      title: sanitiseNameInput(data.title),
      content: sanitiseTextInput(data.content),
      story_date: data.story_date ? sanitiseNameInput(data.story_date) : '',
      story_location: data.story_location
        ? sanitiseTextInput(data.story_location)
        : '',
      tags: data.tags,
      member_ids: data.member_ids ?? [],
      tree_id: treeId,
      created_by: currentUser.uid,
      media_urls: [],
    };

    if (onAddStory) {
      onAddStory(payload);
    }
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Add story
        </Typography>
        <IconButton onClick={closeModal}>
          <ClearIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Capture meaningful memories, traditions, and moments for your family
        tree.
      </Typography>
      <Box
        py={1}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          p: 2.5,
          animation: 'storyCreateFade 220ms cubic-bezier(0.2, 0, 0, 1)',
          '@keyframes storyCreateFade': {
            '0%': { opacity: 0, transform: 'translateY(5px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <form>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="e.g. Grandmother's moonlight stories"
                inputProps={{ maxLength: textLimits.storyTitle }}
              />
            )}
          />

          <Controller
            name="content"
            control={control}
            rules={{ required: 'Content is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Content"
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="dense"
                multiline
                rows={8}
                placeholder="Write your story here..."
                inputProps={{ maxLength: textLimits.storyContent }}
                error={hasReachedCharacterLimit(
                  field.value,
                  textLimits.storyContent,
                )}
                helperText={getCharacterCountHelperText(
                  field.value,
                  textLimits.storyContent,
                )}
              />
            )}
          />
          <Controller
            name="story_date"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Story date"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="e.g. 1950 or June 1967"
              />
            )}
          />
          <Controller
            name="story_location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Story location"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                placeholder="e.g. Lagos, Nigeria"
              />
            )}
          />
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={topTags}
                value={field.value}
                onChange={(_, newValue) => setValue('tags', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Add tags"
                    fullWidth
                    margin="dense"
                    helperText="You can add new tags e.g. marriage, tradition, name, etc."
                  />
                )}
              />
            )}
          />
          <Controller
            name="member_ids"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={treeMembers}
                getOptionLabel={(member) =>
                  [member.first_name, member.middle_name, member.last_name]
                    .filter(Boolean)
                    .join(' ')
                }
                value={treeMembers.filter((member) =>
                  (field.value ?? []).includes(member.id),
                )}
                onChange={(_, newValue) =>
                  setValue(
                    'member_ids',
                    newValue.map((member) => member.id),
                  )
                }
                renderInput={(params) => (
                  <Box py={1}>
                    <TextField
                      {...params}
                      label="Related family members"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select family members"
                      fullWidth
                      margin="dense"
                      helperText="Choose people this story relates to"
                    />
                  </Box>
                )}
              />
            )}
          />

          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                ref={hiddenFileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,audio/aac,audio/mpeg,audio/mp4,audio/ogg,audio/wav"
                style={{ display: 'none' }}
                onChange={handleMediaSelection}
              />
              <Button
                variant="outlined"
                onClick={() => hiddenFileInputRef.current?.click()}
              >
                Add media
              </Button>
              {selectedFiles.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
              )}
            </Box>
            <FormHelperText sx={{ mt: 1, color: 'text.secondary' }}>
              Accepted files: up to 3 photos (JPG, PNG, or WEBP) and voice
              recordings (MP3, WAV, M4A). Image limit: 25MB. Audio limit: 50MB.
            </FormHelperText>
            {uploadError && (
              <FormHelperText sx={{ mt: 1, color: 'error.main' }}>
                {uploadError}
              </FormHelperText>
            )}
          </Box>
        </form>
      </Box>
      <Box
        py={2}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          alignItems: 'center',
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          onClick={closeModal}
          sx={{ borderRadius: 2, minHeight: 40, px: 2, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            minHeight: 40,
            px: 2,
            textTransform: 'none',
            transition:
              'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 3,
            },
          }}
        >
          Add story
        </Button>
      </Box>
    </Box>
  );
}
